"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.DotnetResolver = void 0;
/*---------------------------------------------------------------------------------------------
*  Licensed to the .NET Foundation under one or more agreements.
*  The .NET Foundation licenses this file to you under the MIT license.
*--------------------------------------------------------------------------------------------*/
const os = require("os");
const path = require("path");
const EventStreamEvents_1 = require("../EventStream/EventStreamEvents");
const LocalMemoryCacheSingleton_1 = require("../LocalMemoryCacheSingleton");
const CommandExecutor_1 = require("../Utils/CommandExecutor");
const ExecutableArchitectureDetector_1 = require("../Utils/ExecutableArchitectureDetector");
const TypescriptUtilities_1 = require("../Utils/TypescriptUtilities");
const CacheTimeConstants_1 = require("./CacheTimeConstants");
const DotnetCoreAcquisitionWorker_1 = require("./DotnetCoreAcquisitionWorker");
class DotnetResolver {
    constructor(workerContext, utilityContext, executor) {
        var _a;
        this.workerContext = workerContext;
        this.utilityContext = utilityContext;
        this.executor = executor;
        (_a = this.executor) !== null && _a !== void 0 ? _a : (this.executor = new CommandExecutor_1.CommandExecutor(this.workerContext, this.utilityContext));
    }
    /**
     *
     * @param dotnetExecutablePath The path to the dotnet executable that contains installs of the runtimes and SDKs to search.
     * @param mode The mode to search for, either 'sdk' or 'runtime', or another mode.
     * @param requestedArchitecture The architecture to search for, or undefined to search based on the host architecture, falling back to the os.arch()
     * @returns An array of IDotnetListInfo objects representing the SDKs or runtimes installed on the system.
     */
    getDotnetInstalls(dotnetExecutablePath, mode, requestedArchitecture) {
        return __awaiter(this, void 0, void 0, function* () {
            const oldLookup = process.env.DOTNET_MULTILEVEL_LOOKUP;
            // This is deprecated but still needed to scan .NET 6 and below
            process.env.DOTNET_MULTILEVEL_LOOKUP = '0'; // make it so --list-runtimes only finds the runtimes on that path: https://learn.microsoft.com/en-us/dotnet/core/compatibility/deployment/7.0/multilevel-lookup#reason-for-change
            try {
                if (mode === 'sdk') {
                    const availableSDKs = yield this.getSDKs(dotnetExecutablePath, requestedArchitecture !== null && requestedArchitecture !== void 0 ? requestedArchitecture : DotnetCoreAcquisitionWorker_1.DotnetCoreAcquisitionWorker.defaultArchitecture());
                    if (availableSDKs.length === 0) {
                        return [];
                    }
                    const resolvedHostArchitecture = yield this.resolveHostArchitecture(dotnetExecutablePath, availableSDKs, requestedArchitecture);
                    if (requestedArchitecture && (resolvedHostArchitecture !== requestedArchitecture)) {
                        return [];
                    }
                    // We request to the host to only return the installs that match the architecture for both sdks and runtimes, so they must match now
                    return availableSDKs.map((sdk) => {
                        return Object.assign(Object.assign({}, sdk), { architecture: resolvedHostArchitecture });
                    });
                }
                else {
                    // No need to consider SDKs when looking for runtimes as all the runtimes installed with the SDKs will be included in the runtimes list.
                    const availableRuntimes = yield this.getRuntimes(dotnetExecutablePath, requestedArchitecture !== null && requestedArchitecture !== void 0 ? requestedArchitecture : DotnetCoreAcquisitionWorker_1.DotnetCoreAcquisitionWorker.defaultArchitecture());
                    if (availableRuntimes.length === 0) {
                        return [];
                    }
                    const resolvedHostArchitecture = yield this.resolveHostArchitecture(dotnetExecutablePath, availableRuntimes, requestedArchitecture);
                    if (requestedArchitecture && (resolvedHostArchitecture !== requestedArchitecture)) {
                        return [];
                    }
                    return availableRuntimes.map((runtime) => {
                        return Object.assign(Object.assign({}, runtime), { architecture: resolvedHostArchitecture });
                    });
                }
            }
            finally {
                // Restore the environment variable to its original value
                if (oldLookup !== undefined) {
                    process.env.DOTNET_MULTILEVEL_LOOKUP = oldLookup;
                }
                else {
                    delete process.env.DOTNET_MULTILEVEL_LOOKUP;
                }
            }
        });
    }
    /**
     * Determines the architecture of the .NET installation.
     * @param dotnetExecutablePath The path to the dotnet executable - can be shell relative.
     * @param availableInstalls The list of available .NET installations to the executable.
     * @param requestedArchitecture The architecture requested by the user.
     * @returns The determined architecture or null if it could not be determined.
     */
    resolveHostArchitecture(dotnetExecutablePath, availableInstalls, requestedArchitecture) {
        return __awaiter(this, void 0, void 0, function* () {
            // Resolve the true path, so a path such as 'dotnet' can be resolved as a file to get the architecture of, without needing to call 'which' or 'where'
            const truePath = yield this.resolveTruePath(dotnetExecutablePath, requestedArchitecture !== null && requestedArchitecture !== void 0 ? requestedArchitecture : null);
            if (!truePath) {
                return null;
            }
            // Try to get the arch in the most performant way
            const hostArch = new ExecutableArchitectureDetector_1.ExecutableArchitectureDetector().getExecutableArchitecture(truePath !== null && truePath !== void 0 ? truePath : dotnetExecutablePath);
            const knownArchitecture = ExecutableArchitectureDetector_1.ExecutableArchitectureDetector.IsKnownArchitecture(hostArch);
            if (knownArchitecture) {
                return hostArch;
            }
            else if (yield this.hostSupportsArchFlag(truePath !== null && truePath !== void 0 ? truePath : dotnetExecutablePath, availableInstalls)) {
                return requestedArchitecture !== null && requestedArchitecture !== void 0 ? requestedArchitecture : null;
            }
            else {
                return this.getHostArchitectureViaInfo(dotnetExecutablePath, requestedArchitecture);
            }
        });
    }
    /**
     *
     * @param tentativePath The path to resolve.
     * @param suggestedArchitecture The architecture to resolve the paths for - this is only a suggestion and you must validate afterward
     * @returns The resolved true path or null if not found.
     */
    resolveTruePath(tentativePath, suggestedArchitecture) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            const truePaths = yield this.resolveTruePaths([tentativePath], suggestedArchitecture);
            return (_a = truePaths === null || truePaths === void 0 ? void 0 : truePaths[0]) !== null && _a !== void 0 ? _a : null;
        });
    }
    /**
     *
     * @param tentativePaths Paths that may hold a dotnet executable.
     * @param suggestedArchitecture The architecture to resolve the paths for - this is only a suggestion and you must validate afterward
     * @returns The actual physical location/path on disk where the executables lie for each of the paths.
     * Some of the symlinks etc resolve to a path which works but is still not the actual path.
     */
    resolveTruePaths(tentativePaths, suggestedArchitecture) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            const oldLookup = process.env.DOTNET_MULTILEVEL_LOOKUP;
            // This is deprecated but still needed to scan .NET 6 and below
            process.env.DOTNET_MULTILEVEL_LOOKUP = '0'; // make it so --list-runtimes only finds the runtimes on that path: https://learn.microsoft.com/en-us/dotnet/core/compatibility/deployment/7.0/multilevel-lookup#reason-for-change
            try {
                const truePaths = [];
                suggestedArchitecture !== null && suggestedArchitecture !== void 0 ? suggestedArchitecture : (suggestedArchitecture = DotnetCoreAcquisitionWorker_1.DotnetCoreAcquisitionWorker.defaultArchitecture());
                for (const tentativePath of tentativePaths) {
                    // This will even work if only the sdk is installed, list-runtimes on an sdk installed host would work
                    const runtimeInfo = yield this.getRuntimes(tentativePath, suggestedArchitecture);
                    if (((_a = runtimeInfo === null || runtimeInfo === void 0 ? void 0 : runtimeInfo.length) !== null && _a !== void 0 ? _a : 0) > 0) {
                        // The .NET install layout is a well known structure on all platforms.
                        // See https://github.com/dotnet/designs/blob/main/accepted/2020/install-locations.md#net-core-install-layout
                        //
                        // Therefore we know that the runtime path is always in <install root>/shared/<runtime name>
                        // and the dotnet executable is always at <install root>/dotnet(.exe).
                        //
                        // Since dotnet --list-runtimes will always use the real assembly path to output the runtime folder (no symlinks!)
                        // we know the dotnet executable will be two folders up in the install root.
                        const truePath = path.join(path.dirname(path.dirname(runtimeInfo[0].directory)), (0, TypescriptUtilities_1.getDotnetExecutable)());
                        truePaths.push(truePath);
                        // Preload the cache with the calls we've already done.
                        // Example: 'dotnet' --list-runtimes will be the same as 'C:\\Program Files\\dotnet\\dotnet.exe' --list-runtimes
                        // If the dotnet executable full path was 'C:\\Program Files\\dotnet\\dotnet.exe'.
                        // We do NOT want to do this on Unix, because the dotnet executable is potentially polymorphic.
                        // /usr/local/bin/dotnet becomes /snap/dotnet-sdk/current/dotnet in reality, may have different behavior in shells.
                        if (os.platform() === 'win32') {
                            LocalMemoryCacheSingleton_1.LocalMemoryCacheSingleton.getInstance().aliasCommandAsAnotherCommandRoot(`"${truePath}"`, `"${tentativePath}"`, this.workerContext.eventStream);
                        }
                    }
                    else {
                        this.workerContext.eventStream.post(new EventStreamEvents_1.DotnetFindPathNoRuntimesOnHost(`The host: ${tentativePath} does not contain a .NET runtime installation.`));
                    }
                }
                return truePaths;
            }
            finally {
                // Restore the environment variable to its original value
                if (oldLookup !== undefined) {
                    process.env.DOTNET_MULTILEVEL_LOOKUP = oldLookup;
                }
                else {
                    delete process.env.DOTNET_MULTILEVEL_LOOKUP;
                }
            }
        });
    }
    /**
     *
     * @param hostPath The path to the dotnet executable
     * @returns The architecture of the dotnet host from the PATH, in dotnet info string format
     * The .NET Host will only list versions of the runtime and sdk that match its architecture.
     * Thus, any runtime or sdk that it prints out will be the same architecture as the host.
     * This information is not always accurate as dotnet info is subject to change.
     *
     * @remarks Will return '' if the architecture cannot be determined for some peculiar reason (e.g. dotnet --info is broken or changed).
     */
    // eslint-disable-next-line @typescript-eslint/require-await
    getHostArchitectureViaInfo(hostPath, expectedArchitecture) {
        return __awaiter(this, void 0, void 0, function* () {
            // dotnet --info is not machine-readable and subject to breaking changes. See https://github.com/dotnet/sdk/issues/33697 and https://github.com/dotnet/runtime/issues/98735/
            // Unfortunately even with a new API, that might not go in until .NET 10 and beyond, so we have to rely on dotnet --info for now.
            if (!hostPath || hostPath === '""') {
                return '';
            }
            const infoCommand = CommandExecutor_1.CommandExecutor.makeCommand(`"${hostPath}"`, ['--info']);
            const envWithForceEnglish = process.env;
            envWithForceEnglish.DOTNET_CLI_UI_LANGUAGE = 'en-US';
            // System may not have english installed, but CDK already calls this without issue -- the .NET SDK language invocation is also wrapped by a runtime library and natively includes english assets
            const hostArch = yield (this.executor).execute(infoCommand, { env: envWithForceEnglish, dotnetInstallToolCacheTtlMs: CacheTimeConstants_1.DOTNET_INFORMATION_CACHE_DURATION_MS }, false).then((result) => {
                const lines = result.stdout.split('\n').map((line) => line.trim()).filter(Boolean);
                // This is subject to change but there is no good alternative to do this
                const archLine = lines.find((line) => line.startsWith('Architecture:'));
                if (archLine === undefined) {
                    this.workerContext.eventStream.post(new EventStreamEvents_1.DotnetUnableToCheckPATHArchitecture(`Could not find the architecture of the dotnet host ${hostPath}. If this host does not match the architecture ${expectedArchitecture}:
Please set the PATH to a dotnet host that matches the architecture. An incorrect architecture will cause instability for any C# or .NET related applications that rely on this install.`));
                    if (process.env.DOTNET_INSTALL_TOOL_DONT_ACCEPT_UNKNOWN_ARCH === '1') {
                        return 'unknown'; // Bad value to cause failure mismatch, which will become 'auto'
                    }
                    else {
                        return '';
                    }
                }
                const arch = archLine.split(' ')[1];
                return arch;
            });
            return hostArch;
        });
    }
    /**
     *
     * @param existingPath the path to the executable of the dotnet muxer
     * @param requestedArchitecture the architecture we want SDKs of - not a strict requirement, you must validate afterward
     * @returns An array of IDotnetListInfo objects representing the SDKs installed on the system - arch is always null
     */
    getSDKs(existingPath, requestedArchitecture) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!existingPath || existingPath === '""') {
                return [];
            }
            const findSDKsCommand = CommandExecutor_1.CommandExecutor.makeCommand(`"${existingPath}"`, ['--list-sdks', '--arch', requestedArchitecture]);
            const result = yield (this.executor).execute(findSDKsCommand, { dotnetInstallToolCacheTtlMs: CacheTimeConstants_1.DOTNET_INFORMATION_CACHE_DURATION_MS }, false);
            if (result.status !== '0') {
                return [];
            }
            const sdks = result.stdout.split('\n').map((line) => line.trim()).filter(Boolean);
            const sdkInfos = sdks.map((sdk) => {
                const parts = sdk.split(' ', 2);
                return {
                    mode: 'sdk',
                    version: parts === null || parts === void 0 ? void 0 : parts[0],
                    directory: sdk.split(' ').slice(1).join(' ').slice(1, -1), // need to remove the brackets from the path [path],
                    architecture: null
                };
            }).filter(x => x !== null);
            return sdkInfos;
        });
    }
    hostSupportsArchFlag(dotnetExecutablePath, availableInstalls) {
        return __awaiter(this, void 0, void 0, function* () {
            // https://github.com/dotnet/runtime/pull/116078 --arch was not added until .NET 10 to allow us to skip calling dotnet --info because that is slow, as it is not native code.
            // However, --arch gets ignored if the host does not support it. The output is also identical with or without --arch.
            // After discussion with the runtime team, the best way to determine if the host supports --arch is to call it with an invalid arch to see if it fails, because that only happens when --arch is supported.
            // The --arch flag was added in the middle of .NET 10, so we can assume it is supported if the version is 10.0 or later.
            // We don't want to slow down the current common case for people without .NET 10 by adding another process spawn check.
            // We don't check that the version is 10.0 or later after 2026 when .NET 11 starts rolling out, as It will be slower to check all of the numbers in the output for versions >= 10.
            const hostMaySupportArchFlag = availableInstalls.some(install => install.version.startsWith("10.0") || install.version.startsWith("11.0")) || Date.now() >= new Date('2026-03-01').getTime();
            // Use runtimes instead of sdks, as sdks will always have a runtime, and runtime search can be cached across both mode calls.
            const findInvalidCommand = CommandExecutor_1.CommandExecutor.makeCommand(`"${dotnetExecutablePath}"`, ['--list-runtimes', '--arch', 'invalid-arch']);
            const hostSupportsArchFlag = hostMaySupportArchFlag ? (yield (this.executor).execute(findInvalidCommand, { dotnetInstallToolCacheTtlMs: CacheTimeConstants_1.DOTNET_INFORMATION_CACHE_DURATION_MS }, false)).status !== '0' : false;
            return hostSupportsArchFlag;
        });
    }
    getRuntimesCommand(existingPath, requestedArchitecture) {
        return CommandExecutor_1.CommandExecutor.makeCommand(`"${existingPath}"`, ['--list-runtimes', '--arch', requestedArchitecture]);
    }
    /**
     *
     * @param existingPath the path to the executable of the dotnet muxer
     * @param requestedArchitecture the architecture we want runtimes of - not a strict requirement, you must validate afterward
     * @returns an array of IDotnetListInfo objects representing the runtimes installed on the system - arch is always null
     */
    getRuntimes(existingPath, requestedArchitecture) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!existingPath || existingPath === '""') {
                return [];
            }
            requestedArchitecture !== null && requestedArchitecture !== void 0 ? requestedArchitecture : (requestedArchitecture = DotnetCoreAcquisitionWorker_1.DotnetCoreAcquisitionWorker.defaultArchitecture());
            const windowsDesktopString = 'Microsoft.WindowsDesktop.App';
            const aspnetCoreString = 'Microsoft.AspNetCore.App';
            const runtimeString = 'Microsoft.NETCore.App';
            const result = yield (this.executor).execute(this.getRuntimesCommand(existingPath, requestedArchitecture), { dotnetInstallToolCacheTtlMs: CacheTimeConstants_1.DOTNET_INFORMATION_CACHE_DURATION_MS }, false);
            if (result.status !== '0') {
                return [];
            }
            const runtimes = result.stdout.split('\n').map((line) => line.trim()).filter(Boolean);
            const runtimeInfos = runtimes.map((runtime) => {
                const parts = runtime.split(' ', 3); // account for spaces in PATH, no space should appear before then and luckily path is last
                return {
                    mode: (parts === null || parts === void 0 ? void 0 : parts[0]) === aspnetCoreString ? 'aspnetcore' : parts[0] === runtimeString ? 'runtime' : 'sdk', // sdk is a placeholder for windows desktop, so it can get filtered out until we supported that.
                    version: parts === null || parts === void 0 ? void 0 : parts[1],
                    directory: runtime.split(' ').slice(2).join(' ').slice(1, -1), // account for spaces in PATH, no space should appear before then and luckily path is last.
                    // the 2nd slice needs to remove the brackets from the path [path]
                    architecture: null
                };
            }).filter(x => x !== null);
            return runtimeInfos.filter(x => x.mode !== 'sdk');
        });
    }
}
exports.DotnetResolver = DotnetResolver;
//# sourceMappingURL=DotnetResolver.js.map